import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAm, useEntity, L, Tip, Issues } from "@/components/am/shared";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import StatusControls from "@/components/am/StatusControls";
import ObjectiveAdvisor from "@/components/am/ObjectiveAdvisor";

export default function CampaignForm({ c }) {
  const { opts, issues, tree, setTree } = useAm();
  const { d, set, setD, flush, saving } = useEntity("campaign", c);
  const live = c.on_meta;
  const cbo = d.budget_strategy === "CBO";
  const bkey = d.budget_type === "lifetime" ? "lifetime_budget" : "daily_budget";
  return (
    <div className="space-y-5" data-testid="campaign-form">
      <Head title="Campaign" saving={saving} flush={flush} x={c} level="campaign" />
      <Issues issues={issues} id={c.id} />
      <div><L req>Campaign name</L><Input value={d.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="campaign-name" /></div>
      <div>
        <L req>Objective</L>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {opts.objectives.map((o) => (
            <button key={o.key} disabled={live} onClick={() => set({ objective: o.key })} data-testid={`objective-${o.key}`}
              className={`text-left rounded-lg border p-3 transition-colors disabled:opacity-60 ${d.objective === o.key ? "border-emerald-500 bg-emerald-500/5" : "border-slate-800 hover:bg-slate-900"}`}>
              <div className="text-sm font-semibold">{o.label}</div><div className="text-[11px] text-slate-500">{o.why}</div>
              <div className="text-[10px] font-mono text-slate-500 mt-1">{o.key}</div>
            </button>))}
        </div>
        {live && <p className="text-[11px] text-slate-500 mt-1">Meta does not allow changing the objective after creation. Duplicate the campaign to try another objective.</p>}
        {!live && <div className="mt-3"><ObjectiveAdvisor current={d.objective} onApply={async (rec) => {
          try {
            await api.patch(`/ads-manager/campaign/${c.id}`, { objective: rec.objective, goal: rec.goal });
            let t;
            for (const a of tree.adsets.filter((x) => !x.on_meta)) t = (await api.patch(`/ads-manager/adset/${a.id}`, { destination_type: rec.destination_type, optimization_goal: rec.optimization_goal })).data;
            setD((p) => ({ ...p, objective: rec.objective }));
            if (t) setTree(t);
            toast.success(`${rec.objective_label} applied with ${rec.destination_label} on every draft ad set`);
          } catch (e) { toast.error(errMsg(e)); }
        }} /></div>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><L>Buying type</L>
          <Select value="AUCTION" onValueChange={() => {}}><SelectTrigger data-testid="campaign-buying-type"><SelectValue /></SelectTrigger>
            <SelectContent>{opts.buying_types.map((b) => <SelectItem key={b.key} value={b.key} disabled={!b.supported}>{b.label}{!b.supported ? " (not available for Housing)" : ""}</SelectItem>)}</SelectContent></Select></div>
        <div><L>Special ad category</L>
          <div className="flex gap-2 items-center"><span className="px-3 py-2 rounded-md border border-slate-800 text-sm" data-testid="campaign-housing">Housing (required)</span>
            <Input className="w-24" disabled={live} value={(d.special_ad_category_country || ["IN"])[0]} maxLength={2} onChange={(e) => set({ special_ad_category_country: [e.target.value.toUpperCase()] })} data-testid="campaign-country" />
          </div></div>
      </div>
      <Tip>{opts.housing}</Tip>
      <div className="rounded-xl border border-slate-800 p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div><div className="text-sm font-semibold">Advantage+ campaign budget</div><div className="text-[11px] text-slate-500">On: one budget shared across ad sets. Off: each ad set has its own budget.</div></div>
          <Switch disabled={live} checked={cbo} onCheckedChange={(v) => set({ budget_strategy: v ? "CBO" : "ABO" })} data-testid="campaign-cbo" />
        </div>
        {cbo && (
          <div className="grid sm:grid-cols-3 gap-3">
            <div><L req>Budget type</L><Select disabled={live} value={d.budget_type || "daily"} onValueChange={(v) => set({ budget_type: v })}><SelectTrigger data-testid="campaign-budget-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="daily">Daily budget</SelectItem><SelectItem value="lifetime">Lifetime budget</SelectItem></SelectContent></Select></div>
            <div><L req>{d.budget_type === "lifetime" ? "Lifetime" : "Daily"} budget (INR)</L><Input type="number" min={opts.min_daily} value={d[bkey] ?? ""} onChange={(e) => set({ [bkey]: e.target.value })} data-testid="campaign-budget" /></div>
            <div><L>Bid strategy</L><Select disabled={live} value={d.bid_strategy || "LOWEST_COST_WITHOUT_CAP"} onValueChange={(v) => set({ bid_strategy: v })}><SelectTrigger data-testid="campaign-bid-strategy"><SelectValue /></SelectTrigger>
              <SelectContent>{opts.bid_strategies.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}</SelectContent></Select></div>
          </div>)}
        <div className="grid sm:grid-cols-2 gap-3">
          <div><L>Campaign spending limit (optional, INR)</L><Input type="number" value={d.spend_cap ?? ""} onChange={(e) => set({ spend_cap: e.target.value })} data-testid="campaign-spend-cap" placeholder="No limit" /></div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-800 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div><div className="text-sm font-semibold">A/B test</div><div className="text-[11px] text-slate-500">Compare your ad sets with a Meta split test (needs 2+ ad sets, end dates and a Business Portfolio).</div></div>
        <Switch disabled={live} checked={!!d.ab_test?.enabled} onCheckedChange={(v) => set({ ab_test: { enabled: v } })} data-testid="campaign-ab-test" />
      </div>
    </div>
  );
}

export function Head({ title, saving, flush, x, level }) {
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div><div className="text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
        {x.meta_id && <div className="text-[11px] font-mono text-slate-500" data-testid={`${level}-meta-id`}>Meta ID {x.meta_id}</div>}
        {x.publish_error && <div className="text-xs text-red-600" data-testid={`${level}-publish-error`}>Meta error: {x.publish_error}</div>}
        {x.meta_status?.review_feedback && <div className="text-xs text-red-600">Review feedback: {JSON.stringify(x.meta_status.review_feedback)}</div>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-slate-500" data-testid={`${level}-save-state`}>{saving}</span>
        <button onClick={flush} className="text-xs px-2 py-1 rounded border border-slate-800 hover:bg-slate-900" data-testid={`${level}-save-draft`}>Save draft</button>
        <StatusControls level={level} x={x} />
      </div>
    </div>
  );
}
